<template>
  <div class="tabs-page">
    <!-- Router View for Tab Content -->
    <main class="tab-content">
      <router-view />
    </main>

    <!-- Bottom Navigation with integrated FAB -->
    <BottomNavigation
      :left-tabs="leftTabs"
      :right-tabs="rightTabs"
      :actions="quickActions"
      @action-click="handleActionClick"
    />

    <!-- Modals -->
    <LogExerciseModal
      :is-open="quickAddExerciseOpen"
      :meal-id="quickAddExerciseMealId"
      :meal-plan-id="quickAddExerciseMealPlanId"
      :selected-date="diaryStore.selectedDate"
      @close="closeQuickAddExercise"
      @save="handleQuickExerciseSave"
    />

    <AdHocMealPlanModal
      :is-open="quickAddMealOpen"
      :date="diaryStore.selectedDate"
      @close="quickAddMealOpen = false"
      @save="handleQuickMealSave"
    />

  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { Activity, BarChart3, BookOpen, Settings, UtensilsCrossed } from "lucide-vue-next";
import { useRoute, useRouter } from "vue-router";
import AdHocMealPlanModal from "@/components/AdHocMealPlanModal.vue";
import LogExerciseModal from "@/components/LogExerciseModal.vue";
import BottomNavigation, { type TabItem, type ActionItem } from "@/components/BottomNavigation.vue";
import { useMealsStore, type MealId } from "@/stores/mealsStore";
import { useDiaryStore } from "@/stores/diaryStore";
import { type DiaryEntry, type DiaryMealPlan } from "@/stores/diaryStore";
import { generateId } from "@/utils/id";
const router = useRouter();
const route = useRoute();
const diaryStore = useDiaryStore();
const mealsStore = useMealsStore();

const quickAddMealOpen = ref(false);
const quickAddExerciseOpen = ref(false);
const quickAddExerciseMealId = ref<MealId | null>(null);
const quickAddExerciseMealPlanId = ref<string | null>(null);

// Tab configuration
const leftTabs: TabItem[] = [
  { id: "home", label: "Diary", icon: BookOpen, to: { name: "tabs-diary" } },
  { id: "progress", label: "Dashboard", icon: BarChart3, to: { name: "tabs-dashboard" } },
];

const rightTabs: TabItem[] = [
  { id: "foods", label: "Foods", icon: UtensilsCrossed, to: { name: "tabs-foods" } },
  { id: "settings", label: "Settings", icon: Settings, to: { name: "tabs-settings" } },
];

// Quick actions configuration
const quickActions: ActionItem[] = [
  { id: "food", label: "Add food", icon: UtensilsCrossed },
  { id: "exercise", label: "Add exercise", icon: Activity },
  { id: "meal", label: "Add Meal", icon: BookOpen },
];

const loadMeals = async (): Promise<void> => {
  await mealsStore.load();
};

onMounted(async () => {
  await loadMeals();
});



const handleActionClick = async (actionId: string): Promise<void> => {
  switch (actionId) {
    case "food":
      const query: Record<string, string> = { date: diaryStore.selectedDate };
      const planningTarget = diaryStore.planningAddFoodTarget;
      if (route.name === "tabs-diary" && planningTarget?.date === diaryStore.selectedDate) {
        if (planningTarget.mealPlanId) {
          query.mealPlanId = planningTarget.mealPlanId;
        } else if (planningTarget.mealId !== null) {
          query.mealId = String(planningTarget.mealId);
        }
      }
      await router.push({
        name: "tabs-diary-add-food",
        query,
      });
      break;
    case "exercise":
      if (mealsStore.enabledMeals.length === 0) {
        await loadMeals();
      }
      quickAddExerciseMealId.value = mealsStore.enabledMeals[0]?.id ?? 0;
      quickAddExerciseMealPlanId.value = null;
      quickAddExerciseOpen.value = true;
      break;
    case "meal":
      quickAddMealOpen.value = true;
      break;
  }
};

const handleQuickMealSave = async ({ title, plannedAt }: { title: string; plannedAt: string }): Promise<void> => {
  quickAddMealOpen.value = false;
  const now = new Date().toISOString();
  const plan: DiaryMealPlan = {
    id: generateId(),
    date: diaryStore.selectedDate,
    plannedAt,
    mealId: null,
    title,
    targetCalories: 0,
    targetProtein: 0,
    targetCarbs: 0,
    targetFat: 0,
    createdAt: now,
    updatedAt: now,
  };

  await diaryStore.saveDiaryMealPlan(plan);
  await router.push({ name: "tabs-diary", query: { focusMealPlanId: plan.id } });
};

const closeQuickAddExercise = (): void => {
  quickAddExerciseOpen.value = false;
  quickAddExerciseMealId.value = null;
  quickAddExerciseMealPlanId.value = null;
};

const handleQuickExerciseSave = async (entry: DiaryEntry): Promise<void> => {
  // An exercise added from the nav bar already happened: record it as a
  // completed workout with actuals (start time, duration, burn), not as a
  // loose entry that later gets promoted to a planned-only workout.
  const workoutId = await diaryStore.logCompletedWorkout(entry);
  closeQuickAddExercise();
  await router.push({ name: "tabs-diary", query: { focusMealPlanId: workoutId } });
};
</script>

<style scoped>
/* Tab content area */
.tab-content {
  min-height: 100vh;
  padding-bottom: calc(var(--tab-bar-height) + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)));
  box-sizing: border-box;
}

@media (min-width: 1024px) {
  .tab-content {
    padding-left: 240px;
    padding-bottom: 0;
  }
}
</style>
